import Vue from "vue";
import VueRouter from "vue-router";
import FluidLayout from "@/layout/FluidLayout.vue";
import store from "@/store";
import HomeView from "../views/HomeView.vue";
import { homeRoutes } from "./modules/home";
import { personalRoutes } from "./personalCenter/index";
import { cloudWordRoutes } from "./cloudWord/index";
import { apiWordRoutes } from "./apiWord/index";
import { auditRoutes } from "./auditReport/index";
import { activityRoutes } from "./activityMeet/index";
import { getVisitorRecord } from "@/api/index";
import ApiWordAuthService from "@/utils/guard";
import { goToOfficialWebsite } from "@/utils/domain";
import { clearAuthStorage, isJwtExpired } from "@/utils/auth-token";

Vue.use(VueRouter);

/**
 * 获取当前环境类型
 * @returns {string} 'production' | 'test' | 'local'
 */
const getCurrentEnv = () => {
  const hostname = window.location.hostname;

  // 判断是否为本地环境
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("192.168.")
  ) {
    return "local";
  }

  // 获取环境配置
  const config = {
    officialWebsiteDomain:
      process.env.VUE_APP_OFFICIAL_WEBSITE_DOMAIN || "www.skyshinetech.com",
    blockWatchDomain:
      process.env.VUE_APP_BLOCKWATCH_DOMAIN || "blockwatch.skyshinetech.com",
    testServer: process.env.VUE_APP_TEST_SERVER || "98.130.117.203",
  };

  // 判断是否为生产环境（官网域名或BlockWatch域名）
  if (
    hostname === config.officialWebsiteDomain ||
    hostname === config.blockWatchDomain
  ) {
    return "production";
  }

  // 判断是否为测试环境
  if (hostname === config.testServer) {
    return "test";
  }

  // 默认为生产环境
  return "production";
};

/**
 * 获取环境配置（带默认值）
 */
const getEnvConfig = () => {
  return {
    officialWebsiteDomain:
      process.env.VUE_APP_OFFICIAL_WEBSITE_DOMAIN || "www.skyshinetech.com",
    officialWebsitePort: process.env.VUE_APP_OFFICIAL_WEBSITE_PORT || "6555",
    blockWatchDomain:
      process.env.VUE_APP_BLOCKWATCH_DOMAIN || "blockwatch.skyshinetech.com",
    blockWatchPort: process.env.VUE_APP_BLOCKWATCH_PORT || "6789",
    testServer: process.env.VUE_APP_TEST_SERVER || "98.130.117.203",
    localOfficialPort: process.env.VUE_APP_LOCAL_OFFICIAL_PORT || "8080",
    localBlockWatchPort: process.env.VUE_APP_LOCAL_BLOCKWATCH_PORT || "8081",
  };
};

export const routes = [
  // 首页
  {
    path: "/",
    component: FluidLayout,
    redirect: "/info",
    meta: {
      title: "",
    },
    children: [
      {
        path: "/",
        name: "index",
        meta: {
          title: "天晟科技",
          isShowCal: true,
        },
        components: {
          default: HomeView,
        },
      },
    ],
  },

  // 登录
  {
    path: "/login",
    meta: {
      title: "login",
    },
    components: {
      default: () => import("@/views/login/app-login/login.vue"),
    },
  },
  // 注册
  {
    path: "/register",
    meta: {
      title: "register",
    },
    components: {
      default: () => import("@/views/login/app-register/register.vue"),
    },
  },
  // 修改密码
  {
    path: "/changepwd",
    meta: {
      title: "changepwd",
    },
    components: {
      default: () => import("@/views/login/app-reset/changepwd.vue"),
    },
  },
  {
    path: "/search",
    component: FluidLayout,
    redirect: "/search",
    meta: {
      title: "search",
    },
    children: [
      {
        path: "/search/:name",
        name: "search",
        components: {
          default: () => import("@/views/SearchView.vue"),
        },
      },
      {
        path: "/search",
        name: "searchs",
        components: {
          default: () => import("@/views/SearchView.vue"),
        },
      },
    ],
  },
  ...homeRoutes,
  ...personalRoutes,
  ...cloudWordRoutes,
  ...apiWordRoutes,
  ...auditRoutes,
  ...activityRoutes,
  {
    path: "/*",
    meta: {
      title: "500-服务端错误",
    },
    name: "error-500",
    component: (resolve) => {
      require(["@/views/500.vue"], resolve);
    },
  },
];

const router = new VueRouter({
  mode: "history",
  routes,
});

// 捕获路由导航错误，避免在控制台显示警告
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => {
    // 忽略导航取消错误
    if (err.name !== "NavigationDuplicated") {
      throw err;
    }
  });
};

router.beforeEach((to, from, next) => {
  document.title = to.meta.title || "天晟科技";

  const token = localStorage.getItem("token");
  if (token && isJwtExpired(token)) {
    clearAuthStorage();
    store.commit("SET_LOGIN", false);
    store.commit("SET_VERIFICATION", "0");

    const requiresLogin = ["/controlCenter", "/cloudWord", "/apiWord"].some(
      (path) => to.path.startsWith(path),
    );
    if (requiresLogin) {
      localStorage.setItem("redirectUrl", to.fullPath);
      next("/login");
      return;
    }
  }

  // if (ApiWordAuthService.needsVerification(to)) {
  //   // 显示验证弹窗，不调用 next()
  //   ApiWordAuthService.showAuthModal(to, from, next, router);
  //   return;
  // }

  // 获取当前域名和端口信息
  const hostname = window.location.hostname;
  const port = window.location.port;

  // 获取当前环境类型
  const currentEnv = getCurrentEnv();

  // 获取环境配置（带默认值）
  const envConfig = getEnvConfig();

  // 如果路由中包含/apiWord，检查是否在blockwatch域名/端口上
  if (to.path.startsWith("/apiWord")) {
    const isValidBlockWatchDomain =
      currentEnv === "production"
        ? hostname === envConfig.blockWatchDomain
        : currentEnv === "test"
        ? port === envConfig.blockWatchPort
        : port === envConfig.localBlockWatchPort;

    if (!isValidBlockWatchDomain) {
      // 重定向到官网首页
      if (to.path !== "/") {
        next("/");
      } else {
        next(); // 如果已经在根路径，则继续
      }
      return;
    }
  }
  // 如果路由中不包含/apiWord，检查是否在官网域名/端口上
  else {
    // 特殊处理根路径，避免无限循环
    if (to.path === "/") {
      const isValidOfficialWebsite =
        currentEnv === "production"
          ? hostname === envConfig.officialWebsiteDomain
          : currentEnv === "test"
          ? port === envConfig.officialWebsitePort
          : port === envConfig.localOfficialPort;

      if (!isValidOfficialWebsite) {
        // 跳转到正确的官网地址
        goToOfficialWebsite("/");
      }
      next();
      return;
    }

    // 对于非根路径的非apiWord路由，检查域名/端口
    const isValidOfficialWebsite =
      currentEnv === "production"
        ? hostname === envConfig.officialWebsiteDomain
        : currentEnv === "test"
        ? port === envConfig.officialWebsitePort
        : port === envConfig.localOfficialPort;
    if (!isValidOfficialWebsite) {
      next("/"); // 跳转到官网首页
      return;
    }
  }

  next();
});

router.afterEach((to) => {
  // 记录访客访问（排除登录、注册、修改密码页）
  const excludePaths = ["/login", "/register", "/changepwd"];
  if (!excludePaths.includes(to.path)) {
    getVisitorRecord({ pageUrl: to.path }).catch(() => {});
  }

  // 延迟执行以确保页面渲染完成
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    // 隐藏回到顶部按钮
    const backToTopButton = document.querySelector(".textbtn");
    if (backToTopButton) {
      backToTopButton.style.display = "none";
    }
  }, 100);
});

export default router;
